import { Role } from '@/features/auth/types';

import { useFlag } from '@/lib/api';

import { useCurrentUser } from '@/hooks/useApiKey';

/** Must exist in the license server per deployment before the new flow can appear. */
export const CUSTOMER_FEEDBACK_FORM_FLAG = 'customer_feedback_form_EOL2399';

interface FlagState {
    data?: boolean;
    isLoading?: boolean;
    isError?: boolean;
}

export interface CustomerFeedbackFormFlag {
    /** The new form flow is on. */
    isEnabled: boolean;
    /** The flag has answered (on, off or failed), so an entry point can pick one flow. */
    isResolved: boolean;
}

/** Separate from the hook so the gate can be tested without mocking `@/lib/api`. */
export function isCustomerFeedbackFormEnabled(flag: FlagState): boolean {
    // A failed refetch keeps the last good answer, so an error still means off.
    return flag.data === true && flag.isError !== true;
}

/** A failed lookup counts as resolved (and off); a query that has not started does not. */
export function isCustomerFeedbackFormFlagResolved(flag: FlagState): boolean {
    if (flag.isLoading === true) return false;
    return flag.data !== undefined || flag.isError === true;
}

/** The same test the root route uses: pending is the account's only role. */
export function isAwaitingApproval(roles: readonly Role[] | undefined): boolean {
    return roles?.length === 1 && roles[0] === Role.pending;
}

export function useCustomerFeedbackFormFlag(): CustomerFeedbackFormFlag {
    const [user] = useCurrentUser();
    // An account waiting for approval cannot read flags: the request can only 403 and
    // retry. Skip it, so the flag stays unresolved and neither entry shows. A user
    // that is still loading keeps querying.
    const flag = useFlag(
        CUSTOMER_FEEDBACK_FORM_FLAG,
        !isAwaitingApproval(user?.roles),
    );

    return {
        isEnabled: isCustomerFeedbackFormEnabled(flag),
        isResolved: isCustomerFeedbackFormFlagResolved(flag),
    };
}
